// services/allocationService.js
//
// Allocation targets + rebalance (crypto-backend-api Wave 3-C, feature 14).
//
//   GET  /api/allocation              -> { targets[], totals, orphanedBotIds[] }
//   PUT  /api/allocation              { targets: [{botId, targetPct, minBudget?, maxBudget?}] }
//   POST /api/allocation/rebalance    { dryRun?, totalCapital?, minChange? }
//
// ─── Four things this module exists to stop the UI getting wrong ───────────
//
// 1. **PUT is a FULL REPLACEMENT, not a partial upsert.** Any target NOT in the
//    payload is DELETED. That is what makes "sum <= 100" a real guarantee — a
//    partial upsert could leave stale rows summing past 100 — but it means a
//    screen that PUTs one edited row wipes every other target. So
//    `buildTargetsBody()` takes the WHOLE table, and the response's `removed`
//    array is surfaced rather than ignored.
//
// 2. **A sum below 100 is legal** (the remainder is held in cash). Only a sum
//    ABOVE 100 is a 400. Do not "helpfully" normalise to 100.
//
// 3. **`driftPct` / `equitySharePct` are `null`, not 0, when equity is
//    unknown.** "No data" and "exactly on target" are different facts and must
//    render differently — a null drift shown as a green 0.00% is a lie.
//
// 4. **The first rebalance of a bot may move no money.** The trade engine only
//    STAMPS a baseline on the first budget message it ever sees for a bot
//    (deliberate: deploying the engine must not silently change anyone's buying
//    power). The API reports that as `baselinePending` per target and a
//    `warning` + `baselineOnly: [...]` on the rebalance response. Surface it,
//    or users rebalance over and over wondering why nothing moved.
//
// Also: `excluded` is a non-null STRING when a bot cannot be rebalanced at all
// (base-mode sizing, no quote budget, terminated, or a scanner profile). Those
// bots are never touched by a rebalance and should be shown as such rather than
// silently dropped from the table.
import http from './http';

/** @returns {Promise<{targets:Array, totals:object, orphanedBotIds:Array}>} */
export const getAllocation = () =>
  http.get('/api/allocation').then((res) => {
    const data = res?.data || {};
    return {
      targets: Array.isArray(data.targets) ? data.targets : [],
      totals: data.totals || { totalTargetPct: 0, unallocatedPct: 100, totalBudget: 0, totalEquity: 0, bots: 0 },
      orphanedBotIds: Array.isArray(data.orphanedBotIds) ? data.orphanedBotIds : [],
    };
  });

/** Sum of the target percentages, ignoring blanks. */
export const sumTargets = (rows = []) =>
  rows.reduce((acc, r) => {
    const n = Number(r?.targetPct);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);

/**
 * Validate + shape the PUT body from the full edited table.
 *
 * A row whose `targetPct` is blank is OMITTED, which (given the full-replacement
 * semantics) is how a target is REMOVED. Rows the API marks `excluded` are kept
 * if the user gave them a percentage — the API is the authority on refusing
 * them, and silently dropping a number the user typed is worse than a 400.
 *
 * @returns {{ok:true, body:{targets:Array}, total:number} | {ok:false, errors:string[]}}
 */
export const buildTargetsBody = (rows = []) => {
  const errors = [];
  const targets = [];
  const seen = new Set();

  for (const row of rows) {
    const raw = row?.targetPct;
    if (raw === '' || raw === null || raw === undefined) continue;

    const pct = Number(raw);
    const name = row?.name || `Bot ${row?.botId}`;
    if (!Number.isFinite(pct)) { errors.push(`${name}: target must be a number.`); continue; }
    if (pct < 0 || pct > 100) { errors.push(`${name}: target must be between 0 and 100.`); continue; }
    if (seen.has(row.botId)) { errors.push(`${name}: listed twice.`); continue; }
    seen.add(row.botId);

    const entry = { botId: Number(row.botId), targetPct: pct };

    const min = row.minBudget === '' || row.minBudget == null ? null : Number(row.minBudget);
    const max = row.maxBudget === '' || row.maxBudget == null ? null : Number(row.maxBudget);
    if (min !== null && !Number.isFinite(min)) errors.push(`${name}: min budget must be a number.`);
    if (max !== null && !Number.isFinite(max)) errors.push(`${name}: max budget must be a number.`);
    if (min !== null && max !== null && min > max) errors.push(`${name}: min budget is above max budget.`);
    if (min !== null) entry.minBudget = min;
    if (max !== null) entry.maxBudget = max;

    targets.push(entry);
  }

  const total = targets.reduce((a, t) => a + t.targetPct, 0);
  // Rounded to 4dp before comparing: 33.3333 x 3 must not fail on float dust.
  if (Math.round(total * 10000) / 10000 > 100) {
    errors.push(`Targets add up to ${total.toFixed(2)}%. They cannot exceed 100%.`);
  }

  return errors.length ? { ok: false, errors } : { ok: true, body: { targets }, total };
};

/** Save targets. Does NOT move any money — call rebalance for that. */
export const putAllocation = (body) =>
  http.put('/api/allocation', body).then((res) => res.data || {});

/**
 * @param {{dryRun?:boolean, totalCapital?:number, minChange?:number}} opts
 *   `totalCapital` defaults server-side to the SUM OF THE CURRENT BUDGETS of
 *   the targeted bots — a rebalance redistributes committed money and never
 *   invents capital. Pass a number only to deliberately grow/shrink the pool.
 */
export const rebalance = ({ dryRun = true, totalCapital, minChange } = {}) => {
  const body = { dryRun: Boolean(dryRun) };
  if (totalCapital !== undefined && totalCapital !== null && totalCapital !== '') {
    body.totalCapital = Number(totalCapital);
  }
  if (minChange !== undefined && minChange !== null && minChange !== '') {
    body.minChange = Number(minChange);
  }
  return http.post('/api/allocation/rebalance', body).then((res) => res.data || {});
};

const allocationService = {
  getAllocation,
  putAllocation,
  rebalance,
  buildTargetsBody,
  sumTargets,
};

export default allocationService;
